/**
 * Crypto Market Cycle Indicators
 *
 * Displays key market cycle metrics similar to CoinMarketCap:
 * - Fear & Greed Index (0-100 scale)
 * - Market Cycle Phase (Accumulation, Bull Run, Euphoria, Bear Market)
 * - Bitcoin Halving Countdown & Historical Timeline
 * - Additional on-chain metrics
 */

import { useMemo, useState, useEffect } from 'react';
import { MarketAPI } from '../../services/marketAPI';
import { AdviceBotAPI } from '../../services/adviceBotAPI';
import { useToast } from '../Toast';

// Bitcoin halving history and projections
const HALVING_EVENTS = [
  { date: new Date('2012-11-28'), blockHeight: 210000, reward: 25 },
  { date: new Date('2016-07-09'), blockHeight: 420000, reward: 12.5 },
  { date: new Date('2020-05-11'), blockHeight: 630000, reward: 6.25 },
  { date: new Date('2024-04-20'), blockHeight: 840000, reward: 3.125 },
  { date: new Date('2028-04-15'), blockHeight: 1050000, reward: 1.5625 }, // Estimated
];

interface CryptoMarketCycleIndicatorsProps {
  symbol?: string;
}

interface CycleData {
  fearGreedIndex: number; // 0-100
  phase: 'accumulation' | 'bull' | 'euphoria' | 'bear';
  volatilityIndex: number; // 0-100
  momentumIndex: number; // 0-100
  socialSentiment: number; // 0-100
}

export function CryptoMarketCycleIndicators({
  symbol = 'BTC'
}: CryptoMarketCycleIndicatorsProps) {
  // Toast notifications for API errors
  const toast = useToast();

  // Bitcoin Halving Calculations
  const halvingInfo = useMemo(() => {
    const now = new Date();

    // Find last and next halving
    let lastHalving = HALVING_EVENTS[0];
    let nextHalving = HALVING_EVENTS[HALVING_EVENTS.length - 1];

    for (let i = 0; i < HALVING_EVENTS.length; i++) {
      if (HALVING_EVENTS[i].date <= now) {
        lastHalving = HALVING_EVENTS[i];
      }
      if (HALVING_EVENTS[i].date > now) {
        nextHalving = HALVING_EVENTS[i];
        break;
      }
    }

    // Calculate days
    const daysSinceLast = Math.floor((now.getTime() - lastHalving.date.getTime()) / (1000 * 60 * 60 * 24));
    const daysUntilNext = Math.floor((nextHalving.date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    // Calculate progress (%)
    const totalDaysBetween = Math.floor((nextHalving.date.getTime() - lastHalving.date.getTime()) / (1000 * 60 * 60 * 24));
    const progress = (daysSinceLast / totalDaysBetween) * 100;

    return {
      lastHalving,
      nextHalving,
      daysSinceLast,
      daysUntilNext,
      progress
    };
  }, []);

  // State for real market data
  const [cycleData, setCycleData] = useState<CycleData>({
    fearGreedIndex: 50,
    phase: 'bull',
    volatilityIndex: 50,
    momentumIndex: 50,
    socialSentiment: 50
  });
  const [loading, setLoading] = useState(true);

  // Fetch real market data from backend
  useEffect(() => {
    let mounted = true;

    async function fetchMarketData() {
      try {
        const [fearGreed, volatility, momentum, sentiment] = await Promise.allSettled([
          MarketAPI.getFearGreedIndex(),
          MarketAPI.getVolatilityMetrics(symbol),
          MarketAPI.getMomentumIndicators(symbol),
          AdviceBotAPI.getAggregatedSentiment(symbol)
        ]);

        if (!mounted) return;

        // Extract Fear & Greed Index
        let fearGreedIndex = 50; // Default fallback
        if (fearGreed.status === 'fulfilled' && fearGreed.value) {
          fearGreedIndex = fearGreed.value.value;
        } else if (fearGreed.status === 'rejected') {
          toast('⚠️ Fear & Greed Index unavailable', 'warning', 5000);
        }

        // Extract Volatility Index
        let volatilityIndex = 50;
        if (volatility.status === 'fulfilled' && volatility.value) {
          volatilityIndex = volatility.value.volatilityIndex;
        } else if (volatility.status === 'rejected') {
          toast(`⚠️ Volatility metrics unavailable for ${symbol}`, 'warning', 5000);
        }

        // Extract Momentum Index
        let momentumIndex = 50;
        if (momentum.status === 'fulfilled' && momentum.value) {
          momentumIndex = momentum.value.momentumIndex;
        } else if (momentum.status === 'rejected') {
          toast(`⚠️ Momentum indicators unavailable for ${symbol}`, 'warning', 5000);
        }

        // Extract Social Sentiment (average from all sources)
        let socialSentiment = 50;
        if (sentiment.status === 'fulfilled' && sentiment.value) {
          const sentimentData = sentiment.value;
          // Convert -100 to +100 range to 0-100
          const twitter = (sentimentData.twitter.sentiment + 100) / 2;
          const reddit = (sentimentData.reddit.sentiment + 100) / 2;
          const news = (sentimentData.news.sentiment + 100) / 2;
          socialSentiment = Math.round((twitter + reddit + news) / 3);
        } else if (sentiment.status === 'rejected') {
          toast(`⚠️ Social sentiment unavailable for ${symbol}`, 'warning', 5000);
        }

        // Determine phase based on fear/greed
        let phase: CycleData['phase'] = 'bull';
        if (fearGreedIndex < 30) phase = 'bear';
        else if (fearGreedIndex < 45) phase = 'accumulation';
        else if (fearGreedIndex > 75) phase = 'euphoria';

        setCycleData({
          fearGreedIndex,
          phase,
          volatilityIndex,
          momentumIndex,
          socialSentiment
        });
      } catch (error) {
        console.error('Error fetching market cycle data:', error);
        // Keep default values on error
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    fetchMarketData();

    return () => {
      mounted = false;
    };
  }, [symbol]);

  // Fear & Greed classification
  const getFearGreedLabel = (value: number): string => {
    if (value <= 24) return 'Extreme Fear';
    if (value <= 44) return 'Fear';
    if (value <= 55) return 'Neutral';
    if (value <= 75) return 'Greed';
    return 'Extreme Greed';
  };

  const getFearGreedColor = (value: number): string => {
    if (value <= 24) return '#ef4444'; // Red
    if (value <= 44) return '#fb923c'; // Orange
    if (value <= 55) return '#fbbf24'; // Yellow
    if (value <= 75) return '#a3e635'; // Light green
    return '#22c55e'; // Green
  };

  const getPhaseInfo = (phase: CycleData['phase']) => {
    switch (phase) {
      case 'accumulation':
        return {
          label: 'Accumulation Phase',
          color: '#3b82f6',
          emoji: '📊',
          description: 'Market is consolidating. Smart money is accumulating.'
        };
      case 'bull':
        return {
          label: 'Bull Run',
          color: '#22c55e',
          emoji: '🚀',
          description: 'Market trending upward with strong momentum.'
        };
      case 'euphoria':
        return {
          label: 'Euphoria Phase',
          color: '#eab308',
          emoji: '🔥',
          description: 'Market overheated. Consider taking profits.'
        };
      case 'bear':
        return {
          label: 'Bear Market',
          color: '#ef4444',
          emoji: '🐻',
          description: 'Market in downtrend. DCA opportunities available.'
        };
    }
  };

  const phaseInfo = getPhaseInfo(cycleData.phase);

  // Create gauge arc path
  const createGaugeArc = (value: number, radius: number = 80) => {
    const startAngle = -135; // degrees
    const endAngle = 135; // degrees
    const totalAngle = endAngle - startAngle;
    const valueAngle = startAngle + (value / 100) * totalAngle;

    const startRad = (startAngle * Math.PI) / 180;
    const valueRad = (valueAngle * Math.PI) / 180;

    const x1 = 100 + radius * Math.cos(startRad);
    const y1 = 100 + radius * Math.sin(startRad);
    const x2 = 100 + radius * Math.cos(valueRad);
    const y2 = 100 + radius * Math.sin(valueRad);

    const largeArc = totalAngle * (value / 100) > 180 ? 1 : 0;

    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`;
  };

  return (
    <div>
      {/* Main Indicators Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '16px',
        marginBottom: '16px'
      }}>
        {/* Fear & Greed Index */}
        <div className="glass-card" style={{
          padding: '24px',
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(99, 102, 241, 0.05) 100%)',
          border: '1px solid rgba(139, 92, 246, 0.2)'
        }}>
          <h3 style={{
            fontSize: '14px',
            fontWeight: 600,
            color: 'var(--muted)',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            Fear & Greed Index
          </h3>

          {/* Gauge */}
          <div style={{ position: 'relative', width: '200px', height: '120px', margin: '0 auto' }}>
            <svg width="200" height="120" viewBox="0 0 200 120">
              {/* Background arc */}
              <path
                d={createGaugeArc(100)}
                fill="none"
                stroke="rgba(255, 255, 255, 0.1)"
                strokeWidth="12"
                strokeLinecap="round"
              />
              {/* Value arc */}
              <path
                d={createGaugeArc(cycleData.fearGreedIndex)}
                fill="none"
                stroke={getFearGreedColor(cycleData.fearGreedIndex)}
                strokeWidth="12"
                strokeLinecap="round"
                style={{ filter: 'drop-shadow(0 0 8px currentColor)' }}
              />
              {/* Center value */}
              <text
                x="100"
                y="75"
                textAnchor="middle"
                style={{
                  fontSize: '32px',
                  fontWeight: 700,
                  fill: getFearGreedColor(cycleData.fearGreedIndex)
                }}
              >
                {cycleData.fearGreedIndex}
              </text>
              <text
                x="100"
                y="95"
                textAnchor="middle"
                style={{
                  fontSize: '12px',
                  fill: 'var(--muted)'
                }}
              >
                {getFearGreedLabel(cycleData.fearGreedIndex)}
              </text>
            </svg>
          </div>

          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '8px',
            fontSize: '11px',
            color: 'var(--muted)',
            lineHeight: '1.5'
          }}>
            <strong style={{ color: 'var(--text)' }}>What it means:</strong>
            <br />
            {cycleData.fearGreedIndex <= 44 ? (
              <>Fear indicates potential buying opportunity. Market may be oversold.</>
            ) : cycleData.fearGreedIndex >= 56 ? (
              <>Greed indicates caution. Market may be overbought.</>
            ) : (
              <>Neutral sentiment. Market in equilibrium.</>
            )}
          </div>
        </div>

        {/* Market Cycle Phase */}
        <div className="glass-card" style={{
          padding: '24px',
          background: `linear-gradient(135deg, ${phaseInfo.color}15 0%, ${phaseInfo.color}05 100%)`,
          border: `1px solid ${phaseInfo.color}30`
        }}>
          <h3 style={{
            fontSize: '14px',
            fontWeight: 600,
            color: 'var(--muted)',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            Market Cycle Phase
          </h3>

          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>
              {phaseInfo.emoji}
            </div>
            <div style={{
              fontSize: '20px',
              fontWeight: 700,
              color: phaseInfo.color,
              marginBottom: '8px'
            }}>
              {phaseInfo.label}
            </div>
            <div style={{
              fontSize: '13px',
              color: 'var(--muted)',
              lineHeight: '1.5'
            }}>
              {phaseInfo.description}
            </div>
          </div>

          {/* Phase Progress Bar */}
          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: 'rgba(0, 0, 0, 0.2)',
            borderRadius: '8px'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '8px',
              fontSize: '10px',
              color: 'var(--muted)'
            }}>
              <span>Bear</span>
              <span>Accumulation</span>
              <span>Bull</span>
              <span>Euphoria</span>
            </div>
            <div style={{
              height: '6px',
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '3px',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                height: '100%',
                width: `${(cycleData.fearGreedIndex / 100) * 100}%`,
                background: `linear-gradient(90deg, #ef4444, #3b82f6, #22c55e, #eab308)`,
                borderRadius: '3px',
                transition: 'width 0.3s ease'
              }} />
            </div>
          </div>
        </div>
      </div>

      {/* Additional Metrics */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '12px',
        marginBottom: '16px'
      }}>
        {/* Volatility Index */}
        <div className="glass-card" style={{ padding: '16px' }}>
          <div style={{
            fontSize: '11px',
            color: 'var(--muted)',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <span>📊</span>
            <span>Volatility Index</span>
          </div>
          <div style={{
            fontSize: '24px',
            fontWeight: 700,
            color: cycleData.volatilityIndex > 70 ? '#ef4444' : cycleData.volatilityIndex > 40 ? '#fbbf24' : '#22c55e',
            marginBottom: '8px'
          }}>
            {cycleData.volatilityIndex}
          </div>
          <div style={{
            height: '4px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              width: `${cycleData.volatilityIndex}%`,
              background: cycleData.volatilityIndex > 70 ? '#ef4444' : cycleData.volatilityIndex > 40 ? '#fbbf24' : '#22c55e',
              borderRadius: '2px'
            }} />
          </div>
        </div>

        {/* Momentum Index */}
        <div className="glass-card" style={{ padding: '16px' }}>
          <div style={{
            fontSize: '11px',
            color: 'var(--muted)',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <span>⚡</span>
            <span>Momentum Index</span>
          </div>
          <div style={{
            fontSize: '24px',
            fontWeight: 700,
            color: cycleData.momentumIndex > 70 ? '#22c55e' : cycleData.momentumIndex > 40 ? '#fbbf24' : '#ef4444',
            marginBottom: '8px'
          }}>
            {cycleData.momentumIndex}
          </div>
          <div style={{
            height: '4px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              width: `${cycleData.momentumIndex}%`,
              background: cycleData.momentumIndex > 70 ? '#22c55e' : cycleData.momentumIndex > 40 ? '#fbbf24' : '#ef4444',
              borderRadius: '2px'
            }} />
          </div>
        </div>

        {/* Social Sentiment */}
        <div className="glass-card" style={{ padding: '16px' }}>
          <div style={{
            fontSize: '11px',
            color: 'var(--muted)',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}>
            <span>💬</span>
            <span>Social Sentiment</span>
          </div>
          <div style={{
            fontSize: '24px',
            fontWeight: 700,
            color: cycleData.socialSentiment > 65 ? '#22c55e' : cycleData.socialSentiment > 35 ? '#fbbf24' : '#ef4444',
            marginBottom: '8px'
          }}>
            {cycleData.socialSentiment}
          </div>
          <div style={{
            height: '4px',
            background: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              height: '100%',
              width: `${cycleData.socialSentiment}%`,
              background: cycleData.socialSentiment > 65 ? '#22c55e' : cycleData.socialSentiment > 35 ? '#fbbf24' : '#ef4444',
              borderRadius: '2px'
            }} />
          </div>
        </div>
      </div>

      {/* Bitcoin Halving Countdown */}
      <div className="glass-card" style={{
        padding: '20px',
        marginBottom: '16px',
        background: 'linear-gradient(135deg, rgba(247, 147, 26, 0.1) 0%, rgba(255, 107, 0, 0.05) 100%)',
        border: '1px solid rgba(247, 147, 26, 0.2)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '20px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #f7931a 0%, #ff6b00 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            boxShadow: '0 4px 12px rgba(247, 147, 26, 0.3)'
          }}>
            ₿
          </div>
          <div>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '2px' }}>
              Bitcoin Halving Countdown
            </h3>
            <p style={{ fontSize: '12px', color: 'var(--muted)', margin: 0 }}>
              Block reward event tracker
            </p>
          </div>
        </div>

        {/* Halving Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '12px',
          marginBottom: '16px'
        }}>
          {/* Days Since Last */}
          <div style={{
            padding: '16px',
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.3)',
            borderRadius: '8px'
          }}>
            <div style={{
              fontSize: '11px',
              color: '#10b981',
              fontWeight: 600,
              textTransform: 'uppercase',
              marginBottom: '8px'
            }}>
              ✓ Last Halving
            </div>
            <div style={{ fontSize: '28px', fontWeight: 700, marginBottom: '4px' }}>
              {halvingInfo.daysSinceLast}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '6px' }}>
              days ago
            </div>
            <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
              {halvingInfo.lastHalving.date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
            </div>
            <div style={{
              marginTop: '6px',
              padding: '3px 8px',
              borderRadius: '4px',
              background: 'rgba(16, 185, 129, 0.2)',
              display: 'inline-block',
              fontSize: '10px',
              fontWeight: 600,
              color: '#10b981'
            }}>
              {halvingInfo.lastHalving.reward} BTC
            </div>
          </div>

          {/* Days Until Next */}
          <div style={{
            padding: '16px',
            background: 'rgba(139, 92, 246, 0.1)',
            border: '1px solid rgba(139, 92, 246, 0.3)',
            borderRadius: '8px'
          }}>
            <div style={{
              fontSize: '11px',
              color: '#8b5cf6',
              fontWeight: 600,
              textTransform: 'uppercase',
              marginBottom: '8px'
            }}>
              ⏱ Next Halving
            </div>
            <div style={{ fontSize: '28px', fontWeight: 700, marginBottom: '4px' }}>
              {halvingInfo.daysUntilNext}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '6px' }}>
              days left
            </div>
            <div style={{ fontSize: '11px', color: 'var(--muted)' }}>
              {halvingInfo.nextHalving.date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              })}
            </div>
            <div style={{
              marginTop: '6px',
              padding: '3px 8px',
              borderRadius: '4px',
              background: 'rgba(139, 92, 246, 0.2)',
              display: 'inline-block',
              fontSize: '10px',
              fontWeight: 600,
              color: '#8b5cf6'
            }}>
              {halvingInfo.nextHalving.reward} BTC
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '6px'
          }}>
            <div style={{ fontSize: '11px', color: 'var(--muted)', fontWeight: 600 }}>
              Cycle Progress
            </div>
            <div style={{ fontSize: '13px', fontWeight: 700, color: '#f7931a' }}>
              {halvingInfo.progress.toFixed(1)}%
            </div>
          </div>
          <div style={{
            height: '6px',
            borderRadius: '6px',
            background: 'rgba(255,255,255,0.1)',
            overflow: 'hidden',
            position: 'relative'
          }}>
            <div style={{
              height: '100%',
              width: `${halvingInfo.progress}%`,
              background: 'linear-gradient(90deg, #10b981 0%, #f7931a 50%, #8b5cf6 100%)',
              borderRadius: '6px',
              transition: 'width 1s ease'
            }} />
          </div>
        </div>
      </div>

      {/* Info Panel */}
      <div style={{
        padding: '16px',
        background: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.2)',
        borderRadius: '12px',
        fontSize: '12px',
        color: 'var(--muted)',
        lineHeight: '1.6'
      }}>
        <strong style={{ color: 'var(--text)' }}>💡 About Market Cycle Indicators:</strong>
        <br />
        These indicators help identify where we are in the crypto market cycle:
        <ul style={{ marginTop: '8px', marginBottom: 0, paddingLeft: '20px' }}>
          <li><strong>Fear & Greed Index:</strong> Combines volatility, momentum, social media, and market dominance to gauge overall market sentiment (0 = Extreme Fear, 100 = Extreme Greed)</li>
          <li><strong>Market Phase:</strong> Identifies the current stage of the market cycle to help time entries and exits</li>
          <li><strong>Bitcoin Halving:</strong> Supply reduction event occurring every ~4 years, historically preceding major bull markets</li>
          <li><strong>Volatility & Momentum:</strong> Additional metrics to confirm trend strength and market stability</li>
        </ul>
      </div>
    </div>
  );
}
